import React from 'react'
import { useHandleCopyValue } from '@sketch/modules-common'
import { Tooltip } from '@sketch/components'
import {
  PositionContainer,
  BorderContainer,
  PaddingContainer,
  DimensionContainer,
  BoxText,
  BorderText,
  PositionText,
  NoPaddingText,
  TopSizeText,
  BottomSizeText,
  LeftSizeText,
  RightSizeText,
  TopLeftText,
  TopRightText,
  BottomRightText,
  BottomLeftText,
  BoxSizingText,
  TopMarginArrow,
  BottomMarginArrow,
  LeftMarginArrow,
  RightMarginArrow,
  TopMargin,
  BottomMargin,
  LeftMargin,
  RightMargin,
  ClickableArea,
} from './BoxModel.styles'
import {
  SketchNonGraphicLayerElement,
  SketchTextLayerElement,
  SketchShapeLayerElement,
  SketchSymbolInstanceLayerElement,
  SketchImageLayerElement,
  SketchBasicFrameElement,
} from '../../../../inspector'
import { StackPadding } from '../../../../inspector/types/InspectorTypes/Stack'

type BoxModelProps = {
  element: SketchLayerElement
}

interface PositionBoxProps {
  measurementData: {
    leftMargin?: number
    topMargin?: number
    rightMargin?: number
    bottomMargin?: number
  }
}

interface BorderBoxProps {
  enabledBorders: boolean
  borderRadius?: number[]
  borderSize?: number
}

interface PaddingBoxProps {
  padding?: StackPadding
}

type SketchLayerElement =
  | SketchNonGraphicLayerElement
  | SketchTextLayerElement
  | SketchShapeLayerElement
  | SketchSymbolInstanceLayerElement
  | SketchImageLayerElement

type ElementWithCorners = SketchLayerElement & {
  corners?: {
    radius: number[]
  }
}

const round = (value: number) =>
  parseFloat(parseFloat(value.toString()).toPrecision(4))

const getParentArtboard = (element: SketchLayerElement): SketchLayerElement => {
  if (element.artboardAncestorElement !== undefined) {
    return getParentArtboard(
      element.artboardAncestorElement as SketchLayerElement
    )
  }

  return element
}

const CopiableText = ({ value }: { value: number }) => {
  const {
    handleCopyValue,
    handleEnterCopiableArea,
    handleLeaveCopiableArea,
    copyTooltipText,
    copyTooltipVisible,
  } = useHandleCopyValue(round(value).toString())

  return (
    <Tooltip
      placement="left"
      spacing="10px"
      visible={copyTooltipVisible}
      content={copyTooltipText}
    >
      <ClickableArea
        onClick={handleCopyValue}
        onMouseEnter={handleEnterCopiableArea}
        onMouseLeave={handleLeaveCopiableArea}
      >
        {value}
      </ClickableArea>
    </Tooltip>
  )
}

const PositionBox: React.FC<PositionBoxProps> = ({
  children,
  measurementData,
}) => {
  const { leftMargin, topMargin, rightMargin, bottomMargin } =
    measurementData || {}

  return (
    <PositionContainer>
      <PositionText>position</PositionText>
      <TopMarginArrow />
      <LeftMarginArrow />
      <RightMarginArrow />
      <BottomMarginArrow />
      {topMargin ? (
        <TopMargin>
          <CopiableText value={topMargin} />
        </TopMargin>
      ) : null}
      {bottomMargin ? (
        <BottomMargin>
          <CopiableText value={bottomMargin} />
        </BottomMargin>
      ) : null}
      {leftMargin ? (
        <LeftMargin>
          <CopiableText value={leftMargin} />
        </LeftMargin>
      ) : null}
      {rightMargin ? (
        <RightMargin>
          <CopiableText value={rightMargin} />
        </RightMargin>
      ) : null}

      {children}
    </PositionContainer>
  )
}

const BorderBox: React.FC<BorderBoxProps> = ({
  children,
  enabledBorders,
  borderRadius,
  borderSize,
}) => {
  return (
    <BorderContainer>
      {enabledBorders || borderRadius ? (
        <>
          <BorderText>border</BorderText>

          {enabledBorders && borderSize && (
            <>
              <TopSizeText>
                <CopiableText value={borderSize} />
              </TopSizeText>
              <BottomSizeText>
                <CopiableText value={borderSize} />
              </BottomSizeText>
              <LeftSizeText>
                <CopiableText value={borderSize} />
              </LeftSizeText>
              <RightSizeText>
                <CopiableText value={borderSize} />
              </RightSizeText>
            </>
          )}

          {borderRadius && (
            <>
              <TopLeftText>
                <CopiableText value={borderRadius[0]} />
              </TopLeftText>
              <TopRightText>
                <CopiableText value={borderRadius[1]} />
              </TopRightText>
              <BottomRightText>
                <CopiableText value={borderRadius[2]} />
              </BottomRightText>
              <BottomLeftText>
                <CopiableText value={borderRadius[3]} />
              </BottomLeftText>
            </>
          )}
        </>
      ) : (
        <BorderText>no border</BorderText>
      )}

      {children}
    </BorderContainer>
  )
}

const PaddingBox: React.FC<PaddingBoxProps> = ({ children, padding }) => {
  return (
    <PaddingContainer hasPadding={!!padding}>
      {padding ? (
        <>
          <BoxText>padding</BoxText>
          <TopSizeText>
            <CopiableText value={padding.top} />
          </TopSizeText>
          <BottomSizeText>
            <CopiableText value={padding.bottom} />
          </BottomSizeText>
          <LeftSizeText>
            <CopiableText value={padding.left} />
          </LeftSizeText>
          <RightSizeText>
            <CopiableText value={padding.right} />
          </RightSizeText>
        </>
      ) : (
        <NoPaddingText>no padding</NoPaddingText>
      )}

      {children}
    </PaddingContainer>
  )
}

export const BoxModel = ({ element }: BoxModelProps) => {
  const elementWithCorners = element as ElementWithCorners

  const width = element.layerSize.width
  const height = element.layerSize.height
  const stack =
    'stack' in element
      ? (element.stack as SketchBasicFrameElement['stack'])
      : undefined
  const padding = stack?.padding

  const borders =
    element.style && 'borders' in element.style
      ? element.style?.borders
      : undefined

  const enabledBorders = borders ? borders[0]?.isEnabled : false

  const border = borders ? borders[0] : undefined
  const borderSize = border?.thickness
  const borderRadius =
    elementWithCorners.corners?.radius.sort().toString() !==
    [0, 0, 0, 0].sort().toString()
      ? elementWithCorners.corners?.radius
      : undefined

  const parentArtboardDimensions = getParentArtboard(element).layerSize

  const measurements = {
    leftMargin: element.rootRelativeBoxRect.x,
    topMargin: element.rootRelativeBoxRect.y,
    rightMargin:
      parentArtboardDimensions.width -
      (element.rootRelativeBoxRect.x + element.rootRelativeBoxRect.width),
    bottomMargin:
      parentArtboardDimensions.height -
      (element.rootRelativeBoxRect.y + element.rootRelativeBoxRect.height),
  }

  return (
    <>
      <PositionBox measurementData={measurements}>
        <BorderBox
          borderRadius={borderRadius}
          enabledBorders={enabledBorders}
          borderSize={borderSize}
        >
          <PaddingBox padding={padding}>
            <DimensionContainer>
              {round(width)}x{round(height)}
            </DimensionContainer>
          </PaddingBox>
        </BorderBox>
      </PositionBox>
      <BoxSizingText>border-box</BoxSizingText>
    </>
  )
}
